import { IPriceLocalized } from 'shared/types/Price';
import { getLocale } from './getLocale';

export interface IFormatMoneyObjectOptions {
  displayCurrencySymbol?: boolean;
  roundDownToDollarAmount?: boolean;
  useGrouping?: boolean;
  showCents?: boolean;
  showInUsd?: boolean;
  locale?: string;
}

/**
 * disambiguatedSymbols
 * GOAT wants different symbols than some supplied
 * by formatMoneyObject in the en-US locale.
 * disambiguatedSymbols maps the modified symbol
 * to each currency.
 */
export const disambiguatedSymbols = new Map<string, string>([
  ['AUD', 'A$'],
  ['CAD', 'C$'],
  ['CNY', '¥'],
  ['EUR', '€'],
  ['GBP', '£'],
  ['HKD', 'HK$'],
  ['JPY', '¥'],
  ['KRW', '₩'],
  ['RM', 'MYR'],
  ['SGD', 'S$'],
  ['TWD', 'NT$'],
  ['USD', '$'],
]);

/**
 * suffixCurrencySymbolLocales
 * Locale list for those lans of which the currency symbol is a suffix
 * For example, if the locale is fr, 1 234,56 $
 */
export const suffixCurrencySymbolLocales = new Set(['fr-ca', 'fr-fr', 'it-it', 'de-de']);

/**
 * [replaceCents] replaces the cents values to 00 so we get the nearest whole dollar amount
 * ex: $898.75 -> $898: replaceCents(89875) -> 89800
 *
 * @param amount: number
 */
const replaceCents = (amount: number) => Number(`${amount}`.replace(/\d{2}$/, '00'));

/**
 * formatMoneyObject
 * Formats the standard currency object returned by sneakers into a localized price string
 *
 * @param {object} money
 * @param {number} money.amount - The price amount in cents, potentially in non USD currency
 * @param {number} money.amountUsdCents - The price amount in USD cents
 * @param {string} money.currency - The currency of price.amount (default USD)
 * @param {object} options
 * @param {boolean} options.displayCurrencySymbol - Show/hide the currency symbol (default true)
 * @param {boolean} options.useGrouping - Show/hide group symbol, typically a comma (default true)
 * @param {boolean} options.showCents - hide cents if .00 - bool values will show/hide always
 * @param {boolean} options.roundDownToDollarAmount - rounds down to dollar amt (ex: 898.99->898.00)
 * @returns {string} a formatted price string based on the price object's currency
 */
export const formatMoneyObject = (
  money: IPriceLocalized,
  options: IFormatMoneyObjectOptions = {},
): string => {
  if (!money || typeof Number(money.amount) !== 'number' || Number.isNaN(Number(money.amount))) {
    return '';
  }

  const { amount: amountCurrencyCents, currency = 'USD', amountUsdCents } = money;
  const amount = options?.showInUsd ? amountUsdCents : amountCurrencyCents;
  const {
    displayCurrencySymbol = true,
    roundDownToDollarAmount = false,
    useGrouping = true,
    showCents,
    showInUsd = false,
    locale: _locale,
  } = options;
  const locale = _locale || getLocale();

  const amountValue = roundDownToDollarAmount ? replaceCents(amount) : Number(amount);
  const displayCurrency = showInUsd ? 'USD' : currency;

  const numberOfDigits = (() => {
    if (typeof showCents === 'undefined' || showCents === null) {
      const hasCents = amountValue % 100 !== 0;
      return hasCents ? 2 : 0;
    }
    return showCents ? 2 : 0;
  })();

  const formattedPrice = new Intl.NumberFormat(locale, {
    currency: displayCurrency,
    currencyDisplay: 'symbol',
    maximumFractionDigits: numberOfDigits,
    minimumFractionDigits: numberOfDigits,
    style: 'currency',
    useGrouping,
  }).format(amountValue / 100);
  // when currency is in disambiguatedSymbols we want to overwrite the symbol
  // we use the regex /^[/D]*/ which:
  // looks for any non-numeric characters - [/D]*
  // at the beginning of the string - ^
  // so "$ 12" matches "$ "
  // and "€EUR33.33" matches "€EUR"
  const disambiguatedCurrencySymbol = displayCurrencySymbol
    ? disambiguatedSymbols.get(displayCurrency) || ''
    : '';
  // A special case added for the locale: it-IT
  // for example, originally, 123456 will be formatted as 1.234,56 USD
  // but we want it to be 1.234,56 $
  if (suffixCurrencySymbolLocales.has(locale)) {
    if (locale === 'it-it') {
      return `${formattedPrice.trim()}${
        displayCurrencySymbol ? disambiguatedCurrencySymbol || ' $' : ''
      }`;
    }
    if (disambiguatedCurrencySymbol) {
      return formattedPrice.replace(/\D*$/, ` ${disambiguatedCurrencySymbol}`);
    }
    return displayCurrencySymbol
      ? formattedPrice
      : formattedPrice.replace(/\D*$/, disambiguatedCurrencySymbol);
  }
  return disambiguatedCurrencySymbol || !displayCurrencySymbol
    ? formattedPrice.replace(/^\D*/, disambiguatedCurrencySymbol)
    : formattedPrice;
};
